//@ts-nocheck
import React, { useState, useEffect, useCallback } from "react";
import {
  Checkbox,
  TextField,
  FormControlLabel,
  Box,
  Chip,
  Autocomplete,
  IconButton,
  Tooltip,
} from "@mui/material";

import { post } from "requests";
import CustomModal from "components/CustomModal";
import MDTypography from "components/MDTypography";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDButtonRoot from "components/MDButton/MDButtonRoot";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import InfoIcon from "@mui/icons-material/Info";

interface SignNowComponentProps {
  showModal: boolean;
  onClose: () => void;
  dealApplicationId: string;
  mediaId: any;
  signatureData: any;
  refreshDocs: () => void;
}

const SignNowSignatureModal: React.FC<SignNowComponentProps> = ({
  showModal,
  onClose,
  dealApplicationId,
  mediaId,
  signatureData,
  refreshDocs,
  setLoading,
}) => {
  const [checkboxes, setCheckboxes] = useState([
    { id: "funders", value: "Funder", checked: false },
    { id: "merchantOwners", value: "Merchant Owner(s)", checked: false },
    { id: "salesrep", value: "Representative", checked: false },
    { id: "isobroker", value: "ISO/Broker", checked: false },
  ]);
  const [isInfoHovered, setIsInfoHovered] = useState(false);
  const [emailUsers, setEmailUsers] = useState<any[]>([]);
  const [newEmail, setNewEmail] = useState("");
  const [newSigner, setNewSigner] = useState("");
  const [merchantDropdown, setMerchantDropdown] = useState<any[]>([]);
  const [finalEmailRecipients, setFinalEmailRecipients] = useState<any[]>([]);
  const [signers, setSigners] = useState<any[]>([]);
  const [rolesMap, setRolesMap] = useState<any>({});

  useEffect(() => {
    fetchRecipientsByCategory();
  }, []);

  useEffect(() => {
    setFinalEmailRecipients((prev) =>
      prev.filter((user) => !signers.some((signer) => signer.email === user.email))
    );
  }, [signers]);

  const handleClose = () => {
    refreshDocs(dealApplicationId);
    setNewEmail("");
    setNewSigner("");
    setFinalEmailRecipients([]);
    setSigners([]);
    setRolesMap({});
    onClose();
  };

  const fetchRecipientsByCategory = () => {
    post("fetchRecipientsByCategory", { applicationId: dealApplicationId }).then((responseData) => {
      setEmailUsers(responseData);
      setFinalEmailRecipients(responseData.defaultRecipients || []);
      const merchantOwners = responseData?.merchantOwners || [];
      const funders = responseData?.funders || [];
      const broker = responseData?.isobroker || [];
      const salesrep = responseData?.salesrep || [];
      const dropdownSigners = [...merchantOwners, ...funders, ...broker, ...salesrep];
      setMerchantDropdown(dropdownSigners);
    });
  };

  const handleCheckBoxChange = (index: number) => {
    const updatedCheckboxes = [...checkboxes];
    updatedCheckboxes[index].checked = !updatedCheckboxes[index].checked;
    setCheckboxes(updatedCheckboxes);
    populateEmailSenders(updatedCheckboxes[index].id);
  };

  const handleRemoveUser = (indexToRemove: number) => {
    setFinalEmailRecipients((prevState) => prevState.filter((_, index) => index !== indexToRemove));
  };

  const isValidEmail = (email: string) => {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  };

  const handleAddEmail = () => {
    const isAddable = !finalEmailRecipients.some(
      (recipient) => recipient.email === newEmail.trim()
    );
    if (isValidEmail(newEmail) && isAddable) {
      setFinalEmailRecipients((prevState) => [...prevState, { name: "", email: newEmail.trim() }]);
      setNewEmail("");
    } else {
      alert("Please enter a valid unique email");
    }
  };

  const handleAddSigner = () => {
    if (isValidEmail(newSigner)) {
      const newUser = { name: "", email: newSigner.trim() };
      if (!merchantDropdown.some((usr) => usr.email === newUser.email)) {
        setMerchantDropdown((prevOptions) => [...prevOptions, newUser]);
        const addedUser = {
          email: newUser.email,
          name: newUser.name,
          isSelected: true,
          disabled: !newUser.email,
        };
        OnChangeSelectedUser([...signers, addedUser]);
      } else {
        alert("Email already exists in the dropdown.");
      }
    } else {
      alert("Please enter a valid unique email");
    }
    setNewSigner("");
  };

  const OnChangeSelectedUser = (selectedUsers: any[]) => {
    let requiredRoles = signatureData?.roles;
    if (selectedUsers?.length > requiredRoles?.length) {
      alert(`Only ${requiredRoles.length} signers required for this document.`);
      return;
    }

    let userIndex = 0;
    const tempMap = {};
    requiredRoles.forEach((eachRole) => {
      tempMap[eachRole] = selectedUsers.length > userIndex ? selectedUsers[userIndex].email : null;
      userIndex++;
    });
    setRolesMap(tempMap);
    setSigners(selectedUsers);
  };

  const populateEmailSenders = (id: string) => {
    let recipients = [];
    let recipientsRemoved = [];
    const checkedBox = checkboxes.filter((box) => box.id === id)[0];
    if (checkedBox.checked) {
      const addedUsers = emailUsers && emailUsers[id];
      if (addedUsers) {
        recipients = [
          ...recipients,
          ...addedUsers.filter(
            (user) => user.email && !signers.some((signer) => signer.value.email === user.email)
          ),
        ];
      }
    } else {
      const removedUsers = emailUsers && emailUsers[id];
      if (removedUsers) {
        const checkedUsers = checkboxes
          .filter((box) => box.checked || box.id === id)
          .flatMap((box) => emailUsers[box.id]);
        removedUsers.forEach((eachUser) => {
          const isEmailInList = checkedUsers.some((usr) => usr.email === eachUser.email);
          const isDefaultUser =
            emailUsers &&
            emailUsers.defaultRecipients &&
            emailUsers.defaultRecipients.some((user) => user.email === eachUser.email);
          if (isEmailInList && !isDefaultUser) {
            recipientsRemoved.push(eachUser);
          }
        });
      }
    }
    setFinalEmailRecipients((prevState) => {
      const mergedList = [...prevState, ...recipients].filter(
        (recipient) =>
          !recipientsRemoved.some((removedUser) => removedUser.email === recipient.email)
      );
      const uniqueSet = new Set(mergedList.map((user) => JSON.stringify(user)));
      return Array.from(uniqueSet).map((entry) => JSON.parse(entry));
    });
  };

  const validateEmails = (emails: string[]) => {
    const invalidEmails: string[] = [];
    emails.forEach((email) => {
      if (!isValidEmail(email)) {
        invalidEmails.push(email);
      }
    });
    return invalidEmails;
  };

  const validateRolesMap = (requiredRoles: any[], rolesMap: any) => {
    for (let i = 0; i < requiredRoles.length; i++) {
      const role = requiredRoles[i];
      if (!(role in rolesMap)) {
        alert(`Signer for '${role}' is missing.`);
        return false;
      }

      if (rolesMap[role] === null) {
        alert(`Signer for ${role} is missing`);
        return false;
      }

      if (!isValidEmail(rolesMap[role])) {
        alert(`Invalid email format for ${role}`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = () => {
    if (signers.length < signatureData?.roles?.length) {
      alert(
        "Please select at least " + signatureData?.roles?.length + " Signers for this document."
      );
      return;
    }

    if (!validateRolesMap(signatureData?.roles, rolesMap)) {
      return;
    }

    const viewers = finalEmailRecipients.map((user) => user && user.email);
    const invalidViewers = validateEmails(viewers);

    if (invalidViewers.length > 0) {
      let errorMessage = "Invalid email addresses:";
      if (invalidViewers.length > 0) {
        errorMessage += `\nViewers: ${invalidViewers.join(", ")}`;
      }
      alert(errorMessage);
      return;
    }

    setLoading(true);

    const payload = {
      applicationId: dealApplicationId,
      mediaId: mediaId,
      signers: rolesMap,
      viewers: viewers,
    };

    post("signnow/requestSignature", payload)
      .then((response) => refreshDocs(dealApplicationId))
      .catch((err) => {
        alert(err.message);
      })
      .finally(() => {
        setLoading(false);
        onClose();
      });
  };

  return (
    <CustomModal
      open={showModal}
      onClose={handleClose}
      title="Request Signatures"
      cancelText="Cancel"
      submitText="Submit"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      maxWidth="sm"
      fullWidth={true}
    >
      <MDBox>
        <MDTypography variant="subtitle1" gutterBottom>
          Required Signatures{" "}
          <span style={{ color: "red", fontSize: 18 }}>
            (** {signatureData?.roles?.length} Signer(s) Required **)
          </span>
        </MDTypography>

        <Autocomplete
          multiple
          options={merchantDropdown}
          getOptionLabel={(option) => `${option?.name} (${option?.email})`}
          value={signers}
          onChange={(_, newValue) => OnChangeSelectedUser(newValue)}
          renderInput={(params) => <TextField {...params} label="Select Signers" />}
          renderTags={(value, getTagProps) =>
            value.map((val, index) => {
              const roleName = Object.keys(rolesMap)?.find((key) => rolesMap[key] === val.email);
              const label =
                val?.name && val?.name !== ""
                  ? `${roleName} : ${val?.name}`
                  : `${roleName} : ${val?.email?.substring(0, 6)}...`;

              return <Chip key={index} label={label} {...getTagProps({ index })} size="small" />;
            })
          }
        />

        <Box mt={2} display="flex" alignItems="center">
          <MDTypography fontSize={15}>Add Email Address</MDTypography>
          <TextField
            value={newSigner}
            onChange={(e) => setNewSigner(e.target.value)}
            variant="outlined"
            style={{ marginLeft: 8, width: 300 }}
          />
          <MDButton onClick={handleAddSigner}>Add </MDButton>
        </Box>

        <MDBox mt={3}>
          <MDTypography variant="subtitle1">Document Viewers (Optional)</MDTypography>
          <MDBox display={"flex"}>
            {checkboxes.map((checkbox, index) => (
              <FormControlLabel
                key={index}
                control={
                  <Checkbox
                    checked={checkbox.checked}
                    onChange={() => handleCheckBoxChange(index)}
                  />
                }
                label={checkbox.value}
              />
            ))}
          </MDBox>
        </MDBox>

        <Box mt={3}>
          <MDTypography variant="body1">Email will be sent to:</MDTypography>
          <MDBox borderColor={"gray"} border={"1px solid"} pt={1} minHeight={100} maxHeight={200}>
            {finalEmailRecipients.map((value, index) => (
              <Box key={index} display="flex" alignItems="center">
                <Chip
                  label={value.name ? `${value.name} (${value.email})` : value.email}
                  onDelete={() => handleRemoveUser(index)}
                  deleteIcon={
                    <i className="fa fa-times" style={{ color: "red", fontSize: "16px" }} />
                  }
                  size="small"
                />
              </Box>
            ))}
          </MDBox>
        </Box>
      </MDBox>
    </CustomModal>
  );
};

export default SignNowSignatureModal;
